/**
 * Validation rule builders + a pure validator for the vehicle modal.
 * Rule builders return antd `Form.Item` `rules` arrays; `validateVehicle`
 * is a framework-free check used in tests and for the modal-level gate.
 *
 * @module vehicleModal/utils/vehicleValidation
 */
import dayjs from "dayjs";
import { MIN_VEHICLE_YEAR, VIN_LENGTH } from "../constants";

/** True when a value is a parseable, non-empty number. */
const isNum = v => v !== undefined && v !== null && v !== "" && !Number.isNaN(Number(v));

/** Largest acceptable model year: next year (for early-release models). */
export const maxVehicleYear = () => dayjs().year() + 1;

/**
 * VIN field rules: optional, but when present must be exactly 17 chars and
 * alphanumeric (excluding I/O/Q per the VIN standard).
 * @returns {import("antd").FormRule[]}
 */
export const vinRules = () => [
  {
    validator: (_rule, value) => {
      if (!value) return Promise.resolve();
      const vin = String(value).trim();
      if (vin.length !== VIN_LENGTH) {
        return Promise.reject(new Error(`VIN must be ${VIN_LENGTH} characters`));
      }
      if (!/^[A-HJ-NPR-Z0-9]{17}$/i.test(vin)) {
        return Promise.reject(new Error("VIN contains invalid characters"));
      }
      return Promise.resolve();
    },
  },
];

/**
 * Year rules: required flag configurable; bounded to [1900, currentYear+1].
 * @param {{required?: boolean}} [opts]
 * @returns {import("antd").FormRule[]}
 */
export const yearRules = ({ required = false } = {}) => {
  const rules = [];
  if (required) rules.push({ required: true, message: "Please select Year" });
  rules.push({
    validator: (_rule, value) => {
      if (value === undefined || value === null || value === "") return Promise.resolve();
      const year = Number(value);
      if (Number.isNaN(year) || year < MIN_VEHICLE_YEAR || year > maxVehicleYear()) {
        return Promise.reject(
          new Error(`Year must be between ${MIN_VEHICLE_YEAR} and ${maxVehicleYear()}`)
        );
      }
      return Promise.resolve();
    },
  });
  return rules;
};

/** Required-text rule with a custom message. @returns {import("antd").FormRule[]} */
export const requiredRule = message => [{ required: true, message }];

/**
 * Non-negative numeric rule for mileage / price inputs.
 * @param {string} [label]
 * @returns {import("antd").FormRule[]}
 */
export const nonNegativeNumberRules = (label = "Value") => [
  {
    validator: (_rule, value) => {
      if (value === undefined || value === null || value === "") return Promise.resolve();
      const num = Number(value);
      if (Number.isNaN(num)) return Promise.reject(new Error(`${label} must be a number`));
      if (num < 0) return Promise.reject(new Error(`${label} cannot be negative`));
      return Promise.resolve();
    },
  },
];

/**
 * Framework-free validation of an assembled vehicle, used by the modal to gate
 * Save (and unit-tested directly). Returns the first error per concern.
 *
 * @param {Object} args
 * @param {"inventory"|"request"} args.tab - active modal tab
 * @param {import("../types").Vehicle|null} args.inventorySelection
 * @param {import("../types").VehicleFormValues} args.values - request-tab form values
 * @param {"idle"|"loading"|"success"|"notFound"|"error"} args.vinStatus
 * @returns {{ valid: boolean, errors: string[] }}
 */
export function validateVehicle({ tab, inventorySelection, values = {}, vinStatus = "idle" }) {
  const errors = [];

  if (tab === "inventory") {
    if (!inventorySelection) errors.push("Please select a vehicle from inventory.");
    return { valid: errors.length === 0, errors };
  }

  // Vehicle Request tab: VIN decode success OR manual Year+Make+Model.
  const hasManualCore = Boolean(values.year && values.make && values.model);
  const vinSucceeded = vinStatus === "success";
  if (!vinSucceeded && !hasManualCore) {
    errors.push("Enter a VIN to look up, or provide Year, Make and Model.");
  }

  // VIN, when typed, must be valid.
  if (values.vin) {
    const vin = String(values.vin).trim();
    if (vin.length !== VIN_LENGTH) errors.push(`VIN must be ${VIN_LENGTH} characters.`);
    else if (!/^[A-HJ-NPR-Z0-9]{17}$/i.test(vin)) errors.push("VIN contains invalid characters.");
  }

  // Year bounds.
  if (values.year) {
    const year = Number(values.year);
    if (Number.isNaN(year) || year < MIN_VEHICLE_YEAR || year > maxVehicleYear()) {
      errors.push(`Year must be between ${MIN_VEHICLE_YEAR} and ${maxVehicleYear()}.`);
    }
  }

  // Numeric, non-negative ranges; max >= min.
  const numericFields = [
    ["priceRangeMin", "Price"],
    ["priceRangeMax", "Price"],
    ["mileageRangeMin", "Mileage"],
    ["mileageRangeMax", "Mileage"],
  ];
  numericFields.forEach(([field, label]) => {
    const v = values[field];
    if (v !== undefined && v !== null && v !== "") {
      const num = Number(v);
      if (Number.isNaN(num)) errors.push(`${label} must be a number.`);
      else if (num < 0) errors.push(`${label} cannot be negative.`);
    }
  });
  if (isNum(values.priceRangeMin) && isNum(values.priceRangeMax)) {
    if (Number(values.priceRangeMax) < Number(values.priceRangeMin)) {
      errors.push("Max price cannot be less than min price.");
    }
  }
  if (isNum(values.mileageRangeMin) && isNum(values.mileageRangeMax)) {
    if (Number(values.mileageRangeMax) < Number(values.mileageRangeMin)) {
      errors.push("Max mileage cannot be less than min mileage.");
    }
  }

  return { valid: errors.length === 0, errors };
}
